import { Map, View } from 'ol';
import { Tile } from 'ol/layer';
import 'ol/ol.css';
import { register } from 'ol/proj/proj4';
import { OSM, XYZ } from 'ol/source';
import proj4 from 'proj4';
import { JSX, useEffect, useRef } from 'react';
import useTransientStore from '../../data/transcientStore';
import classes from './styles.module.css';
import './styles.module.css';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import { Draw, Modify, Snap } from 'ol/interaction';
import { bbox as bboxStrategy } from 'ol/loadingstrategy.js';
import { useTranslation } from "../../hooks/useTranslation";

interface MapViewerProps {
	coordinates: [number, number];
	projection: string;
	tileLayer?: 'osm' | 'satellite';
	zoom?: number;
	opacity?: number;
	tileLayerVisible?: boolean;
	children?: JSX.Element;
	centerOnCoordinates?: boolean; // New prop to control when to center
}

export default function MapViewer({
	coordinates,
	projection,
	tileLayer = 'osm',
	zoom = 14,
	opacity = 1,
	tileLayerVisible = true,
	children,
	centerOnCoordinates = false
}: MapViewerProps) {
	//#region Properties

	const selectedAddressSystem = useTransientStore((state) => state.selectedAddressSystem);
	const addrIdFromStore = selectedAddressSystem?.id || 0;

	const { getTranslation } = useTranslation({ pageId: 323, addressSystemId: addrIdFromStore || 0, autoLoad: true });

	proj4.defs(projection, "+proj=utm +zone=35 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs"); // Register the projection for finland
	register(proj4);


	//#endregion

	//#region Hooks

	const mapRef = useRef<HTMLDivElement | null>(null);
	const mapInstanceRef = useRef<Map | null>(null);
	const osmLayerRef = useRef<Tile | null>(null);
	const satelliteLayerRef = useRef<Tile | null>(null);
	const setMap = useTransientStore((state) => state.setMap);

	// Initialize map only once
	useEffect(() => {
		if (!mapRef.current) return;
		if (!coordinates || !projection || zoom === null) return;

		//OpenStreetMap layer
		const osmLayer = new Tile({
			source: new OSM(),
			opacity: opacity,
			visible: true //Initially visible
		});

		//Esri World Imagery with labels
		const satelliteLayer = new Tile({
			source: new XYZ({
				url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
			}),
			visible: false //Initially hidden
		});

		// Store layer references
		osmLayerRef.current = osmLayer;
		satelliteLayerRef.current = satelliteLayer;

		const source = new VectorSource({ wrapX: false, strategy: bboxStrategy });
		const vectorLayer = new VectorLayer({
			source: source,
			visible: true,
			zIndex: 15,  // Above feature groups (10) but below selection/hover (40, 50)
			properties: { name: 'drawing-layer' }  // Identify this as the drawing layer
		});

		const mapObj = new Map({
			target: mapRef.current,
			view: new View({
				projection: 'EPSG:3857',
				center: [coordinates[0], coordinates[1]],	// [2857529.364488623, 8487719.103925403],
				zoom: zoom
			}),
			layers: [osmLayer, satelliteLayer, vectorLayer]
		});

		// Create interactions with stopclick to prevent doubleclick zoom interference
		const drawPolygon = new Draw({
			source: source,
			type: 'Polygon',
			stopClick: true
		});
		const drawMultiLineString = new Draw({
			source: source,
			type: 'MultiLineString',
			stopClick: true
		});
		const drawPoint = new Draw({
			source: source,
			type: 'Point',
			stopClick: true
		});
		const modify = new Modify({ source: source });
		const snap = new Snap({ source: source });
		drawPolygon.setProperties({ name: 'Polygon' });
		drawMultiLineString.setProperties({ name: 'MultiLineString' });
		drawPoint.setProperties({ name: 'Point' });
		modify.setProperties({ name: 'Modify' });
		snap.setProperties({ name: 'Snap' });

		// Add interactions to the map
		mapObj.addInteraction(drawPolygon);
		mapObj.addInteraction(drawMultiLineString);
		mapObj.addInteraction(drawPoint);
		mapObj.addInteraction(modify);
		mapObj.addInteraction(snap);

		// Disable interactions by default
		mapObj.getInteractions().forEach((interaction) => {
			if (interaction instanceof Draw || interaction instanceof Modify || interaction instanceof Snap) {
				interaction.setActive(false);
			}
		});

		mapInstanceRef.current = mapObj;
		setMap(mapObj);

		return () => {
			mapObj.setTarget(undefined);
			mapInstanceRef.current = null;
			osmLayerRef.current = null;
			satelliteLayerRef.current = null;
		};
	}, [projection]); // Only depend on projection for initialization

	// Handle coordinate changes when explicitly requested
	useEffect(() => {
		if (centerOnCoordinates && mapInstanceRef.current && coordinates) {
			const view = mapInstanceRef.current.getView();
			view.setCenter([coordinates[0], coordinates[1]]);
			view.setZoom(zoom);
		}
	}, [coordinates, zoom, centerOnCoordinates]);

	// Handle tile layer changes
	useEffect(() => {
		if (!osmLayerRef.current || !satelliteLayerRef.current) return;

		//Update layer visibility when `tileLayer` changes
		if (tileLayerVisible === false) {
			osmLayerRef.current.setVisible(false);
			satelliteLayerRef.current.setVisible(false);
		} else {
			osmLayerRef.current.setVisible(tileLayer === 'osm');
			satelliteLayerRef.current.setVisible(tileLayer === 'satellite');
		}
	}, [tileLayer, tileLayerVisible]);

	//#endregion

	return (
		<div className={classes.map} ref={mapRef} id='ol-map'>
			{children}
		</div>
	);
}
